import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Divider,
  Alert
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { CHARTER_SECTION_THEMES, COLORS, glassCardSx, surfaceInsetSx, accordionSummarySx, sectionTitleSx, alertInfoSx } from './clientPortalTheme';

const formatDateTime = (date, time) => {
  if (!date) return '—';
  const d = new Date(date).toLocaleDateString();
  return time ? `${d} ${time}` : d;
};

const CharterCard = ({ charter, defaultExpanded = false, accentColor = COLORS.indigo }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{
        mb: 1.5,
        borderRadius: '16px !important',
        overflow: 'hidden',
        ...glassCardSx,
        border: `1.5px solid ${COLORS.glassBorderStrong}`,
        '&:before': { display: 'none' },
        boxShadow: expanded ? '0 12px 32px rgba(0, 0, 0, 0.1)' : glassCardSx.boxShadow
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore sx={{ color: accentColor }} />}
        sx={{ ...accordionSummarySx }}
      >
        <Box display="flex" alignItems="center" gap={2} width="100%" pr={2} flexWrap="wrap">
          <Typography fontWeight={700} sx={{ color: COLORS.text }}>
            {charter.charterNumber || 'No number'} — {charter.title}
          </Typography>
          <Chip
            label={charter.flightType?.replace(/_/g, ' ')}
            size="small"
            sx={{ borderRadius: 999, borderColor: accentColor, color: accentColor }}
            variant="outlined"
          />
          <Chip
            label={charter.status?.replace(/_/g, ' ')}
            size="small"
            sx={{ borderRadius: 999, bgcolor: accentColor, color: COLORS.white, fontWeight: 600 }}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {charter.clientName && (
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Client</Typography>
              <Typography sx={{ color: COLORS.text }}>{charter.clientName}</Typography>
            </Grid>
          )}
          {charter.specialRequirements && (
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Special Requirements</Typography>
              <Typography sx={{ color: COLORS.textSecondary }}>{charter.specialRequirements}</Typography>
            </Grid>
          )}
          {charter.passengers?.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ color: COLORS.textMuted }}>
                Trip Passengers ({charter.passengers.length})
              </Typography>
              <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                {charter.passengers.map((p) => p.name || 'Unnamed').join(', ')}
              </Typography>
            </Grid>
          )}
        </Grid>

        <Divider sx={{ mb: 2, borderColor: COLORS.glassBorder }} />
        <Typography variant="subtitle2" gutterBottom sx={{ color: COLORS.text }}>Sectors</Typography>
        {(charter.sectors || []).map((sector) => (
          <Paper key={sector._id} variant="outlined" sx={{ p: 2, mb: 1, borderRadius: 3, borderLeft: `3px solid ${COLORS.indigo}`, ...surfaceInsetSx }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography fontWeight={600} sx={{ color: COLORS.text }}>
                Sector {sector.seqNo}: {sector.departure?.airport} → {sector.arrival?.airport}
              </Typography>
              <Chip label={sector.status?.replace(/_/g, ' ')} size="small" />
            </Box>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Departure</Typography>
                <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                  {formatDateTime(sector.departure?.date, sector.departure?.time)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: COLORS.textMuted }}>ETA</Typography>
                <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                  {formatDateTime(sector.arrival?.estimatedDate, sector.arrival?.estimatedTime)}
                </Typography>
              </Grid>
              {sector.wakeTurbulence && (
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Wake Turbulence</Typography>
                  <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                    {sector.wakeTurbulence}
                  </Typography>
                </Grid>
              )}
              {sector.flightRules && (
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Flight Rules</Typography>
                  <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                    {sector.flightRules}
                  </Typography>
                </Grid>
              )}
              {sector.assignedPilots?.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Crew</Typography>
                  <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                    {sector.assignedPilots.map((p) => `${p.firstName} ${p.lastName}`).join(', ')}
                  </Typography>
                </Grid>
              )}
              {sector.passengers?.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Passengers</Typography>
                  <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                    {sector.passengers.map((p) => p.name).join(', ')}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

const CharterSection = ({ title, charters, defaultExpandFirst, sectionKey }) => {
  if (!charters?.length) return null;
  const theme = CHARTER_SECTION_THEMES[sectionKey] || CHARTER_SECTION_THEMES.upcoming;
  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          ...glassCardSx,
          px: 2,
          py: 1.25,
          mb: 1.5,
          borderLeft: `3px solid ${COLORS.indigo}`,
          borderRadius: 3
        }}
      >
        <Typography variant="h6" fontWeight={800} sx={{ color: COLORS.text }}>
          {title} ({charters.length})
        </Typography>
      </Box>
      {charters.map((charter, idx) => (
        <CharterCard
          key={charter._id}
          charter={charter}
          defaultExpanded={defaultExpandFirst && idx === 0}
          accentColor={theme.accent}
        />
      ))}
    </Box>
  );
};

const ClientCharterPanel = ({ charters, loading }) => {
  if (loading) return null;

  const total = (charters?.active?.length || 0)
    + (charters?.upcoming?.length || 0)
    + (charters?.completed?.length || 0);

  if (total === 0) {
    return <Alert severity="info" sx={{ ...alertInfoSx }}>No charter history found for this aircraft.</Alert>;
  }

  return (
    <Box>
      <CharterSection title="Active Charters" charters={charters.active} defaultExpandFirst sectionKey="active" />
      <CharterSection title="Upcoming Charters" charters={charters.upcoming} sectionKey="upcoming" />
      <CharterSection title="Completed Charters" charters={charters.completed} sectionKey="completed" />
    </Box>
  );
};

export default ClientCharterPanel;
